import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip as MuiTooltip,
} from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';
import { useApi } from '@backstage/core-plugin-api';
import { Link } from '@backstage/core-components';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CodeIcon from '@mui/icons-material/Code';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import GitHubIcon from '@mui/icons-material/GitHub';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { DateRangePicker, DateRange, getDefaultDateRange } from './DateRangePicker';
import { PRMetrics } from './types';
import { HealthLoadingState } from './HealthLoadingState';
import { teamMetricsApiRef } from '../../../../../../api/teamMetricsApi';
import { calculateSimpleWeeklyPRData } from './utils';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(4),
    backgroundColor: '#fff',
    minHeight: '100%',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(4),
  },
  headerTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  titleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  backButton: {
    padding: theme.spacing(1),
    '&:hover': {
      backgroundColor: '#F7FAFC',
    },
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    backgroundColor: '#3182CE',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: 20,
    fontWeight: 700,
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  },
  titleText: {
    display: 'flex',
    flexDirection: 'column',
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: 12,
    color: '#8E9299',
    marginBottom: theme.spacing(0.5),
  },
  breadcrumbBold: {
    fontWeight: 700,
    color: '#1A1A1A',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  titleName: {
    fontSize: 24,
    fontWeight: 700,
    color: '#1A1A1A',
  },
  roleChip: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    backgroundColor: '#EBF8FF',
    color: '#2B6CB0',
    border: '1px solid #BEE3F8',
    height: 22,
  },
  summaryCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(4),
  },
  summaryCard: {
    padding: theme.spacing(3),
    border: '1px solid #E2E8F0',
    borderRadius: 4,
    backgroundColor: '#fff',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  },
  cardLabelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  cardLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: '#8E9299',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
  cardInfoIcon: {
    fontSize: 14,
    color: '#CBD5E0',
  },
  cardValueRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
  },
  cardValue: {
    fontSize: 36,
    fontWeight: 700,
    color: '#1A1A1A',
  },
  cardValueLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: '#8E9299',
  },
  cardSubtext: {
    fontSize: 10,
    color: '#8E9299',
    marginTop: theme.spacing(0.5),
  },
  chartsRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(4),
  },
  chartCard: {
    padding: theme.spacing(3),
    border: '1px solid #E2E8F0',
    borderRadius: 4,
    backgroundColor: '#fff',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  },
  chartTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: '#8E9299',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    marginBottom: theme.spacing(3),
  },
  chartContainer: {
    height: 280,
  },
  serviceBreakdown: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    maxHeight: 250,
    overflowY: 'auto',
  },
  serviceItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1),
    border: '1px solid #F1F1F1',
    borderRadius: 4,
    transition: 'border-color 0.2s',
    '&:hover': {
      borderColor: '#BEE3F8',
    },
  },
  serviceInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  serviceColorBar: {
    width: 4,
    height: 24,
    borderRadius: 2,
  },
  serviceNameContainer: {},
  serviceName: {
    fontSize: 14,
    fontWeight: 700,
    color: '#1A1A1A',
    cursor: 'pointer',
    transition: 'color 0.2s',
    '&:hover': {
      color: '#3182CE',
    },
  },
  serviceLines: {
    fontSize: 10,
    fontWeight: 700,
    color: '#8E9299',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
  serviceStats: {
    textAlign: 'right',
  },
  servicePRs: {
    fontSize: 10,
    fontWeight: 700,
    color: '#8E9299',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
  prTableSection: {
    border: '1px solid #E2E8F0',
    borderRadius: 4,
    backgroundColor: '#fff',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    overflow: 'hidden',
    marginBottom: theme.spacing(4),
  },
  prTableHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 3),
    borderBottom: '1px solid #E2E8F0',
    backgroundColor: '#F7FAFC',
  },
  prTableTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: '#8E9299',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
  tableHead: {
    backgroundColor: '#fff',
  },
  headerCell: {
    fontSize: 10,
    fontWeight: 700,
    color: '#8E9299',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    borderBottom: '1px solid #E2E8F0',
  },
  tableRow: {
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#F7FAFC',
    },
    '&:hover $externalLink': {
      color: '#0070F3',
    },
  },
  cell: {
    borderBottom: '1px solid #E2E8F0',
    padding: theme.spacing(2),
  },
  prTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#1A1A1A',
  },
  prId: {
    fontSize: 10,
    fontFamily: 'monospace',
    color: '#8E9299',
  },
  serviceCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  serviceIcon: {
    fontSize: 14,
    color: '#4A5568',
  },
  serviceText: {
    fontSize: 12,
    color: '#4A5568',
  },
  serviceLink: {
    fontSize: 12,
    fontWeight: 600,
    color: '#0070F3',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  changesCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    fontFamily: 'monospace',
    fontSize: 10,
    fontWeight: 700,
  },
  additions: {
    color: '#38A169',
  },
  deletions: {
    color: '#E53E3E',
  },
  dateText: {
    fontSize: 12,
    color: '#8E9299',
  },
  externalLink: {
    fontSize: 16,
    color: '#CBD5E0',
    transition: 'color 0.2s',
  },
  infoBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    backgroundColor: '#EBF8FF',
    border: '1px solid #BEE3F8',
    borderRadius: 4,
  },
  infoIcon: {
    fontSize: 16,
    color: '#3182CE',
    marginTop: 2,
  },
  infoText: {
    fontSize: 12,
    color: '#2B6CB0',
    lineHeight: 1.5,
  },
}));

interface UserServiceBreakdown {
  name: string;
  contributions: number;
  linesContributed: number;
  color: string;
}

interface UserHealthDashboardProps {
  weaveEmail: string;
  onBack: () => void;
  hideBackButton?: boolean;
}

const SERVICE_COLORS = ['#3182CE', '#38A169', '#805AD5', '#D69E2E', '#E53E3E', '#00B5D8'];

export const UserHealthDashboard: React.FC<UserHealthDashboardProps> = ({
  weaveEmail,
  onBack,
  hideBackButton = false,
}) => {
  const classes = useStyles();
  const teamMetricsApi = useApi(teamMetricsApiRef);

  const [loading, setLoading] = useState(true);
  const [userPRs, setUserPRs] = useState<PRMetrics[]>([]);
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange);

  const displayName = useMemo(() => {
    return weaveEmail.split('@')[0];
  }, [weaveEmail]);

  useEffect(() => {
    let cancelled = false;

    const fetchUserPRs = async () => {
      setLoading(true);
      try {
        const response = await teamMetricsApi.GenerateUserPRReport({
          usernames: [weaveEmail],
          startDate: dateRange.start,
        });

        if (!cancelled) {
          setUserPRs(response.prs);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchUserPRs();

    return () => {
      cancelled = true;
    };
  }, [weaveEmail, dateRange.start, teamMetricsApi]);

  const serviceBreakdown = useMemo((): UserServiceBreakdown[] => {
    const breakdown: Record<string, { contributions: number; linesContributed: number }> = {};

    userPRs.forEach((pr) => {
      if (!breakdown[pr.service]) {
        breakdown[pr.service] = { contributions: 0, linesContributed: 0 };
      }
      breakdown[pr.service].contributions += 1;
      breakdown[pr.service].linesContributed += pr.additions;
    });

    return Object.entries(breakdown)
      .map(([name, data], index) => ({
        name,
        contributions: data.contributions,
        linesContributed: data.linesContributed,
        color: SERVICE_COLORS[index % SERVICE_COLORS.length],
      }))
      .sort((a, b) => b.contributions - a.contributions);
  }, [userPRs]);

  const totalLinesAdded = useMemo(() => {
    return userPRs.reduce((acc, pr) => acc + pr.additions, 0);
  }, [userPRs]);

  const totalLinesRemoved = useMemo(() => {
    return userPRs.reduce((acc, pr) => acc + pr.deletions, 0);
  }, [userPRs]);

  const weeklyPRData = useMemo(
    () => calculateSimpleWeeklyPRData(userPRs, dateRange.start),
    [userPRs, dateRange.start],
  );

  if (loading) {
    return <HealthLoadingState />;
  }

  return (
    <Box className={classes.container}>
      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.headerTop}>
          <Box className={classes.titleSection}>
            {!hideBackButton && (
              <IconButton className={classes.backButton} onClick={onBack}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box className={classes.avatar}>{displayName.charAt(0).toUpperCase()}</Box>
            <Box className={classes.titleText}>
              <Box className={classes.breadcrumb}>
                <span>Catalog</span>
                <span>/</span>
                <span>Users</span>
                <span>/</span>
                <span className={classes.breadcrumbBold}>{displayName}</span>
              </Box>
              <Box className={classes.title}>
                <Typography className={classes.titleName}>{displayName}'s Quality Dashboard</Typography>
                <Chip label="Individual Contributor" size="small" className={classes.roleChip} />
              </Box>
            </Box>
          </Box>

          <DateRangePicker dateRange={dateRange} onChange={setDateRange} />
        </Box>
      </Box>

      {/* Info Box */}
      <Box className={classes.infoBox} sx={{ mb: 3 }}>
        <InfoOutlinedIcon className={classes.infoIcon} />
        <Typography className={classes.infoText}>
          <strong>Note:</strong> These metrics show activity, not performance. The numbers here are
          interesting data points for context but quality of work, collaboration, and impact are not captured by PR counts or lines of code.
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Box className={classes.summaryCards}>
        <Paper className={classes.summaryCard} elevation={0}>
          <Box className={classes.cardLabelRow}>
            <Typography className={classes.cardLabel}>Pull Requests</Typography>
            <MuiTooltip title="Total number of PRs merged to the default branch of a repo by this contributor during the selected period.">
              <InfoOutlinedIcon className={classes.cardInfoIcon} />
            </MuiTooltip>
          </Box>
          <Box className={classes.cardValueRow}>
            <Typography className={classes.cardValue}>{userPRs.length}</Typography>
            <Typography className={classes.cardValueLabel}>Merged</Typography>
          </Box>
          <Typography className={classes.cardSubtext}>During selected period</Typography>
        </Paper>

        <Paper className={classes.summaryCard} elevation={0}>
          <Box className={classes.cardLabelRow}>
            <Typography className={classes.cardLabel}>Repositories</Typography>
            <MuiTooltip title="Number of distinct repositories this contributor has merged PRs to.">
              <InfoOutlinedIcon className={classes.cardInfoIcon} />
            </MuiTooltip>
          </Box>
          <Box className={classes.cardValueRow}>
            <Typography className={classes.cardValue}>{serviceBreakdown.length}</Typography>
            <Typography className={classes.cardValueLabel}>Contributed to</Typography>
          </Box>
          <Typography className={classes.cardSubtext}>Across the organization</Typography>
        </Paper>

        <Paper className={classes.summaryCard} elevation={0}>
          <Box className={classes.cardLabelRow}>
            <Typography className={classes.cardLabel}>Lines Changed</Typography>
            <MuiTooltip title="Total lines of code added and removed across all PRs.">
              <InfoOutlinedIcon className={classes.cardInfoIcon} />
            </MuiTooltip>
          </Box>
          <Box className={classes.cardValueRow}>
            <Typography className={classes.cardValue} style={{ color: '#38A169' }}>
              +{totalLinesAdded.toLocaleString()}
            </Typography>
            <Typography className={classes.cardValue} style={{ color: '#E53E3E', marginLeft: 16 }}>
              -{totalLinesRemoved.toLocaleString()}
            </Typography>
          </Box>
          <Typography className={classes.cardSubtext}>During selected period</Typography>
        </Paper>
      </Box>

      {/* Charts Row - PR Activity and Repos */}
      <Box className={classes.chartsRow}>
        {/* PR Activity Chart */}
        <Paper className={classes.chartCard} elevation={0}>
          <Typography className={classes.chartTitle}>PR Activity</Typography>
          <Box className={classes.chartContainer}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weeklyPRData} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                <XAxis
                  dataKey="week"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fill: '#8E9299' }}
                  interval={Math.max(0, Math.floor(weeklyPRData.length / 8))}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fill: '#8E9299' }}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: 4,
                    border: '1px solid #E2E8F0',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    fontSize: 12,
                  }}
                  formatter={(value: number) => [`${value} PRs`, 'Count']}
                />
                <Bar dataKey="prCount" radius={[4, 4, 0, 0]}>
                  {weeklyPRData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.prCount > 0 ? '#3182CE' : '#E2E8F0'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>

        {/* Repositories Contributed To */}
        <Paper className={classes.chartCard} elevation={0}>
          <Typography className={classes.chartTitle}>Repositories Contributed To</Typography>
          <Box className={classes.serviceBreakdown}>
            {serviceBreakdown.map((service) => (
              <Box key={service.name} className={classes.serviceItem}>
                <Box className={classes.serviceInfo}>
                  <Box
                    className={classes.serviceColorBar}
                    style={{ backgroundColor: service.color }}
                  />
                  <Box className={classes.serviceNameContainer}>
                    <Link
                      to={`/catalog/default/component/${service.name}/health`}
                      className={classes.serviceName}
                    >
                      {service.name}
                    </Link>
                    <Typography className={classes.serviceLines}>
                      {service.linesContributed.toLocaleString()} lines added
                    </Typography>
                  </Box>
                </Box>
                <Box className={classes.serviceStats}>
                  <Typography className={classes.servicePRs}>{service.contributions} PRs</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>

      {/* PR Activity Table */}
      <Paper className={classes.prTableSection} elevation={0}>
        <Box className={classes.prTableHeader}>
          <Typography className={classes.prTableTitle}>Recent Pull Request Activity</Typography>
          <GitHubIcon style={{ fontSize: 16, color: '#8E9299' }} />
        </Box>
        <TableContainer>
          <Table>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell className={classes.headerCell}>Pull Request</TableCell>
                <TableCell className={classes.headerCell}>Repository</TableCell>
                <TableCell className={classes.headerCell} align="right">
                  Changes
                </TableCell>
                <TableCell className={classes.headerCell} align="right">
                  Date
                </TableCell>
                <TableCell className={classes.headerCell} width={40}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {userPRs.map((pr) => (
                <TableRow key={pr.id} className={classes.tableRow}>
                  <TableCell className={classes.cell}>
                    <Typography className={classes.prTitle}>{pr.title}</Typography>
                    <Typography className={classes.prId}>{pr.id}</Typography>
                  </TableCell>
                  <TableCell className={classes.cell}>
                    <Box className={classes.serviceCell}>
                      <CodeIcon className={classes.serviceIcon} />
                      <Link
                        to={`/catalog/default/component/${pr.service}/health`}
                        className={classes.serviceLink}
                      >
                        {pr.service}
                      </Link>
                    </Box>
                  </TableCell>
                  <TableCell className={classes.cell} align="right">
                    <Box className={classes.changesCell}>
                      <span className={classes.additions}>+{pr.additions}</span>
                      <span className={classes.deletions}>-{pr.deletions}</span>
                    </Box>
                  </TableCell>
                  <TableCell className={classes.cell} align="right">
                    <Typography className={classes.dateText}>{pr.date}</Typography>
                  </TableCell>
                  <TableCell className={classes.cell} align="right">
                    {pr.prUrl ? (
                      <IconButton
                        size="small"
                        onClick={() => window.open(pr.prUrl, '_blank', 'noopener,noreferrer')}
                        style={{ padding: 4 }}
                      >
                        <OpenInNewIcon className={classes.externalLink} />
                      </IconButton>
                    ) : (
                      <OpenInNewIcon className={classes.externalLink} />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

    </Box>
  );
};

export default UserHealthDashboard;
